import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

import {AsyncSearchCombobox} from "@/components/ui/async-search-combobox";

import {useInfinitePatrolRoutes} from "../hooks/use-infinite-patrol-routes";

export type PatrolRouteOption = {id: number; name: string};

type Props = {
  value: PatrolRouteOption | null;
  onChange: (value: PatrolRouteOption | null) => void;
  disabled?: boolean;
};

export function PatrolRouteCombobox({value, onChange, disabled}: Props) {
  const {t} = useTranslation("patrol_route");
  const {t: tCommon} = useTranslation("common");
  const [search, setSearch] = useState("");
  const filters = useMemo(
    () => ({
      active: 1,
      ...(search.trim() ? {q: search.trim()} : {}),
    }),
    [search],
  );
  const {
    routes,
    hasNextPage,
    fetchNextPage,
    isPending,
    isFetchingNextPage,
  } = useInfinitePatrolRoutes({
    filters,
    sort: "name",
    enabled: !disabled,
  });

  const items = useMemo<PatrolRouteOption[]>(() => {
    const mapped = routes.map((r) => ({id: r.patrol_route_id, name: r.name}));
    if (value && !mapped.some((item) => item.id === value.id)) {
      return [value, ...mapped];
    }
    return mapped;
  }, [routes, value]);

  return (
    <AsyncSearchCombobox<PatrolRouteOption>
      items={items}
      value={value}
      onChange={onChange}
      onSearch={setSearch}
      onLoadMore={fetchNextPage}
      hasMore={hasNextPage}
      isLoading={!disabled && isPending}
      isLoadingMore={isFetchingNextPage}
      disabled={disabled}
      getItemKey={(item) => String(item.id)}
      getItemLabel={(item) => item.name}
      isItemEqual={(item, selected) => item.id === selected?.id}
      placeholder={t("form.select_route_placeholder")}
      emptyMessage="No active routes found."
      loadingMessage={tCommon("loading")}
      loadingMoreMessage={tCommon("loading")}
      showClear={false}
    />
  );
}
